'use client';

import { T } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { Modal } from '@flipster/react/design-system/modal';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import MailIcon from '@flipster/react/icon/generated/MailIcon';
import TelegramIcon from '@flipster/react/icon/generated/TelegramIcon';
import WhatsAppIcon from '@flipster/react/icon/generated/WhatsappIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

const messageTemplate = `Hello VIP Team 💙

I'm interested in the Flipster VIP Program.

- Flipster UID:
- Current VIP tier on other exchanges (if applicable):
- Additional questions or requests:`;

const emailSubject = '[VIP Program] Membership Inquiry';

const encodedMessage = encodeURIComponent(messageTemplate);
const encodedWhatsAppMessage = encodeURIComponent(messageTemplate.replace(' 💙', ','));
const encodedSubject = encodeURIComponent(emailSubject);

const contactChannels = [
  {
    id: 'telegram',
    name: <T id='Common.r251107.Telegram' />,
    value: '@selenaa_flipster',
    icon: TelegramIcon,
    href: `https://t.me/selenaa_flipster?text=${encodedMessage}`,
  },
  {
    id: 'whatsapp',
    name: <T id='Vip.r260121.WhatsApp' />,
    value: '+6581005051',
    icon: WhatsAppIcon,
    href: `https://wa.me/6581005051?text=${encodedWhatsAppMessage}`,
  },
  {
    id: 'email',
    name: <T id='Common.r250710.Email' />,
    value: 'vip@flipster.io',
    icon: MailIcon,
    href: `mailto:vip@flipster.io?subject=${encodedSubject}&body=${encodedMessage}`,
  },
] as const;

export const ContactChannelModal = () => {
  const { isOpen, close, unmount } = useOverlayItem();
  return (
    <Modal.Root isOpen={isOpen} onClose={close} onExit={unmount}>
      <Modal.Header>
        <Modal.Title label={<T id='Vip.r260121.Choose_your_preferred_contact_channel' />} />
      </Modal.Header>
      <Modal.Content css={{ pt: 16 }}>
        <div className={stack({ gap: 24 })}>
          {contactChannels.map((channel) => (
            <a
              key={channel.id}
              href={channel.href}
              target='_blank'
              rel='noopener noreferrer'
              className={hstack({
                gap: 16,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                _hover: { opacity: 0.7 },
              })}
            >
              <channel.icon size={32} color={token('colors.content.default.level-5')} />
              <div className={stack({ gap: 2, flex: 1 })}>
                <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>{channel.name}</p>
                <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                  {channel.value}
                </p>
              </div>
              <ChevronRightIcon size={16} color={token('colors.content.default.level-3')} />
            </a>
          ))}
          <div className={hstack({ gap: 8, p: 12, rounded: 8, bg: 'background.state.warning.tint' })}>
            <CautionCircleIcon size={16} className={css({ color: 'content.state.warning' })} />
            <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
              <T id='Vip.r260121.Flipster_does_not_ask_for_your_password_OTP_or_seed_phrase' />
            </p>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
